import React, { useState, useRef, useEffect } from 'react';
import { FiMessageSquare, FiX, FiSend, FiCpu, FiRefreshCw, FiMaximize, FiMinimize } from 'react-icons/fi';
import api from '../../api';
import './Chatbot.css';

const INITIAL_MSG = { id: 1, sender: 'leo', text: "Welcome back! I am Leo, your smart library assistant. How can I help you today?", options: ["Search book Economics", "Active members", "Top fines"] };

const Chatbot = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);
    
    // Load from local storage or use initial
    const [messages, setMessages] = useState(() => {
        try {
            const saved = localStorage.getItem('leo_messages');
            return saved ? JSON.parse(saved) : [INITIAL_MSG];
        } catch (e) {
            return [INITIAL_MSG];
        }
    });

    const [input, setInput] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const messagesEndRef = useRef(null);

    // Persist messages to local storage on change
    useEffect(() => {
        localStorage.setItem('leo_messages', JSON.stringify(messages));
    }, [messages]);

    const toggleChat = () => {
        setIsOpen(!isOpen);
        if (isOpen) setIsFullScreen(false); // Reset fullscreen when closing
    };

    const toggleFullScreen = () => setIsFullScreen(!isFullScreen);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        if (isOpen) scrollToBottom();
    }, [messages, isOpen, isTyping]);

    const resetChat = () => setMessages([{ ...INITIAL_MSG, id: Date.now() }]);

    const handleSend = async (e, directText = null) => {
        if (e) e.preventDefault();
        const textToSend = directText || input;
        if (!textToSend.trim()) return;

        const userMsg = { id: Date.now(), sender: 'user', text: textToSend };
        setMessages(prev => [...prev, userMsg]);
        setInput('');
        setIsTyping(true);

        try {
            // Send full history for context
            const history = messages.map(m => ({ role: m.sender, content: m.text }));
            const res = await api.post('/chat.php', { message: userMsg.text, history });
            const botText = res.data.reply || "I'm not sure how to respond to that.";
            const options = res.data.options || null;
            
            setMessages(prev => [...prev, {
                id: Date.now() + 1,
                sender: 'leo',
                text: botText,
                options: options
            }]);
        } catch (err) {
            setMessages(prev => [...prev, {
                id: Date.now() + 1,
                sender: 'leo',
                text: "Sorry, I'm having trouble connecting to my database right now."
            }]);
        } finally {
            setIsTyping(false);
        }
    };

    return (
        <div className="leo-chatbot-container">
            {isOpen ? (
                <div className={`leo-chat-window ${isFullScreen ? 'is-fullscreen' : ''}`}>
                    <div className="leo-chat-header">
                        <div className="leo-header-info">
                            <div className="leo-avatar"><FiCpu /></div>
                            <div>
                                <h3>Leo</h3>
                                <p>Smart Library Assistant</p>
                            </div>
                        </div>
                        <div className="leo-header-actions">
                            <button className="leo-action-btn" onClick={toggleFullScreen} title={isFullScreen ? "Exit Fullscreen" : "Fullscreen"} aria-label="Toggle Fullscreen">
                                {isFullScreen ? <FiMinimize /> : <FiMaximize />}
                            </button>
                            <button className="leo-action-btn" onClick={resetChat} title="Restart Chat" aria-label="Restart Chat">
                                <FiRefreshCw />
                            </button>
                            <button className="leo-action-btn leo-close-btn" onClick={toggleChat} aria-label="Close Chat">
                                <FiX />
                            </button>
                        </div>
                    </div>

                    <div className="leo-chat-body">
                        {messages.map(msg => (
                            <div key={msg.id} className="leo-message-group">
                                <div className={`leo-message-wrapper ${msg.sender === 'user' ? 'leo-wrapper-user' : 'leo-wrapper-leo'}`}>
                                    {msg.sender === 'leo' && <div className="leo-message-avatar"><FiCpu /></div>}
                                    <div className={`leo-message ${msg.sender === 'user' ? 'leo-message-user' : 'leo-message-leo'}`} dangerouslySetInnerHTML={{ __html: msg.text.replace(/\n/g, '<br/>') }} />
                                </div>
                                {msg.options && msg.options.length > 0 && !isTyping && msg === messages[messages.length - 1] && (
                                    <div className="leo-suggestion-chips">
                                        {msg.options.map((opt, idx) => (
                                            <button key={idx} className="leo-chip" onClick={() => handleSend(null, opt)}>
                                                {opt}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                        {isTyping && (
                            <div className="leo-message-wrapper leo-wrapper-leo">
                                <div className="leo-message-avatar"><FiCpu /></div>
                                <div className="leo-message leo-message-leo">
                                    <span className="leo-typing-dot"></span>
                                    <span className="leo-typing-dot"></span>
                                    <span className="leo-typing-dot"></span>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    <form className="leo-chat-footer" onSubmit={handleSend}>
                        <input
                            type="text"
                            placeholder="Ask me something..."
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            disabled={isTyping}
                        />
                        <button type="submit" disabled={!input.trim() || isTyping}>
                            <FiSend />
                        </button>
                    </form>
                </div>
            ) : (
                <button className="leo-fab" onClick={toggleChat} aria-label="Open Chat">
                    <FiMessageSquare />
                </button>
            )}
        </div>
    );
};

export default Chatbot;
